import apiClient from "@/src/services/api/axios";
import type {
  UdsPolicyGetApiResponse,
  UdsPolicyGetResponse,
  UdsPolicyRule,
  UdsPolicyUpdatePayload,
} from "@/src/models/permissions.model";

const PROJECT_KEY = process.env.EXPO_PUBLIC_X_BLOCKS_KEY ?? "";

const SCHEMA_NAME = "starTransaction";
const POLICY_NAME = "min-max-transection";
const POLICY_DESCRIPTION = "Generated from Rule Builder";

const OPERATION_CREATE = 1; // Create access control (operation 0 was View)
const POLICY_TYPE = 0;
const PRIORITY = 1;
const LOGICAL_OPERATOR_AND = 0; // "All the following rules match"
const LEFT_SOURCE_SCHEMA_FIELD = 1;
const RIGHT_SOURCE_STATIC_VALUE = 2;
const OPERATOR_GTE = 3; // Greater Than or Equal
const OPERATOR_LTE = 5; // Less Than or Equal

const STAR_TRANSACTION_SCHEMA_ID =
  process.env.EXPO_PUBLIC_STAR_TRANSACTION_SCHEMA_ID ??
  "7500d034-b54d-44d4-8225-709f59f37ccd";

// `policy/update` upserts: with `itemId` it edits, without it creates.
type PolicyUpsertPayload = Omit<UdsPolicyUpdatePayload, "itemId"> & {
  itemId?: string;
};

const buildAmountRules = (
  minGiveAmt: number,
  maxGiveAmt: number,
): UdsPolicyRule[] => [
  {
    leftSource: LEFT_SOURCE_SCHEMA_FIELD,
    leftOperand: "amount",
    operator: OPERATOR_GTE,
    rightSource: RIGHT_SOURCE_STATIC_VALUE,
    rightOperand: "",
    staticValue: String(minGiveAmt),
  },
  {
    leftSource: LEFT_SOURCE_SCHEMA_FIELD,
    leftOperand: "amount",
    operator: OPERATOR_LTE,
    rightSource: RIGHT_SOURCE_STATIC_VALUE,
    rightOperand: "",
    staticValue: String(maxGiveAmt),
  },
];

const fetchStarTransactionPolicies = async (): Promise<
  ReadonlyArray<UdsPolicyGetResponse>
> => {
  const res = await apiClient.get<UdsPolicyGetApiResponse>(
    `/uds/v1/data-access/policy/${SCHEMA_NAME}/get`,
    { params: { projectKey: PROJECT_KEY } },
  );
  return res.data?.data ?? [];
};

const reloadUdsConfigurations = (): Promise<unknown> =>
  apiClient.post(`/uds/v1/dbujnb/configurations/${PROJECT_KEY}/reload`, {});

export const syncStarTransactionAmountPolicy = async (
  minGiveAmt: number,
  maxGiveAmt: number,
): Promise<void> => {
  const policies = await fetchStarTransactionPolicies();
  const existing = policies.find((p) => p.policyName === POLICY_NAME);
  const schemaId = existing?.schemaId ?? STAR_TRANSACTION_SCHEMA_ID;

  const payload: PolicyUpsertPayload = {
    ...(existing?.itemId ? { itemId: existing.itemId } : {}),
    policyName: POLICY_NAME,
    policyDescription: POLICY_DESCRIPTION,
    policyType: POLICY_TYPE,
    operation: OPERATION_CREATE,
    schemaName: SCHEMA_NAME,
    schemaId,
    fieldNames: [],
    ruleGroup: {
      logicalOperator: LOGICAL_OPERATOR_AND,
      rules: buildAmountRules(minGiveAmt, maxGiveAmt),
      nestedGroups: [],
    },
    priority: PRIORITY,
    isAllowPolicy: true,
    projectKey: PROJECT_KEY,
  };

  await apiClient.post("/uds/v1/data-access/policy/update", payload);
  await reloadUdsConfigurations();
};
